/**
 * Individual message component with reactions, actions, and image attachments.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemDecorator,
  Chip,
  Tooltip,
  Modal,
  ModalClose,
  Sheet,
} from '@mui/joy';
import {
  DotsThreeVertical as MoreVertIcon,
  PencilSimple as EditIcon,
  Trash as DeleteIcon,
  ArrowBendUpLeft as ReplyIcon,
  Smiley as AddReactionIcon,
} from '@phosphor-icons/react';
import type { Message, MessageAttachment } from '../../types/chat';

interface MessageItemProps {
  message: Message;
  isOwnMessage?: boolean;
  hideThreadIndicator?: boolean;
  onReaction?: (messageId: string, emoji: string) => void;
  onRemoveReaction?: (messageId: string, emoji: string) => void;
  onEdit?: (messageId: string, content: string) => void;
  onDelete?: (messageId: string) => void;
  onReply?: (messageId: string) => void;
}

// Common emoji reactions
const QUICK_REACTIONS = ['rofl', 'thumbsup', 'exclamation', 'heart', 'cursing', 'fire', 'hangten'];

const EMOJI_MAP: Record<string, string> = {
  rofl: '🤣',
  thumbsup: '👍',
  exclamation: '❗',
  heart: '❤️',
  cursing: '🤬',
  fire: '🔥',
  hangten: '🤙',
};

// Regex to match emoji characters (covers most common emoji)
const EMOJI_REGEX = /(?:\p{Emoji_Presentation}|\p{Emoji}\uFE0F)/gu;

// Check if message contains only emojis (with optional whitespace)
const isEmojiOnlyMessage = (content: string): { isEmojiOnly: boolean; emojiCount: number } => {
  const trimmed = content.trim();
  if (!trimmed) return { isEmojiOnly: false, emojiCount: 0 };

  // Remove all emojis and whitespace
  const withoutEmojis = trimmed.replace(EMOJI_REGEX, '').replace(/\s/g, '');

  // If nothing remains, it's emoji-only
  const isEmojiOnly = withoutEmojis.length === 0;

  // Count emojis
  const emojiMatches = trimmed.match(EMOJI_REGEX);
  const emojiCount = emojiMatches ? emojiMatches.length : 0;

  return { isEmojiOnly, emojiCount };
};

// Get first and last initials from a name (e.g., "Jeffrey Stilwell" -> "JS")
const getInitials = (name: string | undefined): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0][0]?.toUpperCase() || '?';
  }
  const first = parts[0][0]?.toUpperCase() || '';
  const last = parts[parts.length - 1][0]?.toUpperCase() || '';
  return first + last;
};

// Generate a consistent color based on a string (name/username)
const AVATAR_COLORS = [
  '#7C3AED', // violet
  '#2563EB', // blue
  '#0891B2', // cyan
  '#059669', // emerald
  '#D97706', // amber
  '#DC2626', // red
  '#DB2777', // pink
  '#7C3AED', // purple
];

const getAvatarColor = (name: string | undefined): string => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const MessageItem: React.FC<MessageItemProps> = ({
  message,
  isOwnMessage = false,
  hideThreadIndicator = false,
  onReaction,
  onRemoveReaction,
  onEdit,
  onDelete,
  onReply,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [showReactions, setShowReactions] = useState(false);
  const [previewImage, setPreviewImage] = useState<MessageAttachment | null>(null);

  const formatTime = (timestamp: string): string => {
    return new Date(timestamp).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Parse message content and highlight @mentions
  const renderContent = (content: string) => {
    // Match @mentions (@ followed by name that may include spaces until end or another @)
    const mentionRegex = /@([A-Za-z][A-Za-z\s]*?)(?=\s@|\s{2}|$|[.,!?])/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(content)) !== null) {
      // Add text before the mention
      if (match.index > lastIndex) {
        parts.push(content.slice(lastIndex, match.index));
      }

      // Add the highlighted mention
      const mentionName = match[1].trim();
      parts.push(
        <Box
          key={match.index}
          component="span"
          sx={{
            bgcolor: 'primary.softBg',
            color: 'primary.softColor',
            px: 0.5,
            borderRadius: 'xs',
            fontWeight: 'md',
          }}
        >
          @{mentionName}
        </Box>
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }

    return parts.length > 0 ? parts : content;
  };

  const handleReactionClick = (emoji: string) => {
    // Check if user already reacted
    const existingReaction = message.reaction_summary?.find(
      (r) => r.emoji === emoji && r.user_reacted
    );

    if (existingReaction) {
      onRemoveReaction?.(message.id, emoji);
    } else {
      onReaction?.(message.id, emoji);
    }
    setShowReactions(false);
  };

  const renderReactionSummary = () => {
    if (!message.reaction_summary?.length) return null;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
        {message.reaction_summary.map((reaction) => (
          <Tooltip
            key={reaction.emoji}
            title={reaction.users.map((u) => u.full_name || u.username).join(', ')}
          >
            <Chip
              size="sm"
              variant={reaction.user_reacted ? 'soft' : 'plain'}
              color={reaction.user_reacted ? 'primary' : 'neutral'}
              onClick={() => handleReactionClick(reaction.emoji)}
              sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: 'background.level2' },
              }}
            >
              {EMOJI_MAP[reaction.emoji] || reaction.emoji} {reaction.count}
            </Chip>
          </Tooltip>
        ))}
      </Box>
    );
  };

  if (message.is_deleted) {
    return (
      <Box sx={{ py: 1, px: 2 }}>
        <Typography level="body-sm" color="neutral" fontStyle="italic">
          This message was deleted
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: { xs: 1, md: 1.5 },
        py: 0.5,
        px: { xs: 1, md: 2 },
        '&:hover': {
          bgcolor: 'background.level1',
        },
        '&:hover .message-actions': {
          opacity: 1,
        },
      }}
    >
      {/* Avatar */}
      <Avatar
        size="sm"
        src={message.sender?.avatar_url || undefined}
        alt={message.sender?.full_name || message.sender?.username || 'User'}
        sx={{
          mt: 0.5,
          bgcolor: message.sender?.avatar_url ? undefined : getAvatarColor(message.sender?.full_name || message.sender?.username),
          color: 'white',
        }}
      >
        {getInitials(message.sender?.full_name || message.sender?.username)}
      </Avatar>

      {/* Message Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography level="title-sm" fontWeight="lg">
            {message.sender?.full_name || message.sender?.username || 'Unknown'}
          </Typography>
          <Typography level="body-xs" color="neutral">
            {formatTime(message.created_at)}
          </Typography>
          {message.is_edited && (
            <Typography level="body-xs" color="neutral" fontStyle="italic">
              (edited)
            </Typography>
          )}
        </Box>

        {/* Content */}
        {(() => {
          const { isEmojiOnly, emojiCount } = isEmojiOnlyMessage(message.content);
          const isLargeEmoji = isEmojiOnly && emojiCount <= 5;

          return (
            <Typography
              level="body-md"
              sx={{
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                ...(isLargeEmoji && {
                  fontSize: '2.25rem', // 150% of default (~1.5rem)
                  lineHeight: 1.2,
                }),
              }}
            >
              {isLargeEmoji ? message.content : renderContent(message.content)}
            </Typography>
          );
        })()}

        {/* Attachments */}
        {message.attachments && message.attachments.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {message.attachments.map((attachment, index) => (
              <Box
                key={`${attachment.url}-${index}`}
                onClick={() => setPreviewImage(attachment)}
                sx={{
                  cursor: 'pointer',
                  maxWidth: 300,
                  maxHeight: 200,
                  borderRadius: 'sm',
                  overflow: 'hidden',
                  bgcolor: 'background.level2',
                  '&:hover': {
                    opacity: 0.9,
                  },
                }}
              >
                <Box
                  component="img"
                  src={attachment.url}
                  alt={attachment.name}
                  sx={{
                    display: 'block',
                    maxWidth: '100%',
                    maxHeight: 200,
                    objectFit: 'contain',
                  }}
                />
              </Box>
            ))}
          </Box>
        )}

        {/* Thread indicator */}
        {!hideThreadIndicator && message.reply_count > 0 && (
          <Box
            sx={{
              mt: 0.5,
              cursor: 'pointer',
              color: 'primary.plainColor',
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={() => onReply?.(message.id)}
          >
            <Typography level="body-sm">
              {message.reply_count} {message.reply_count === 1 ? 'reply' : 'replies'}
            </Typography>
          </Box>
        )}

        {/* Reactions */}
        {renderReactionSummary()}
      </Box>

      {/* Actions */}
      <Box
        className="message-actions"
        sx={{
          opacity: { xs: 1, md: 0 },
          transition: 'opacity 0.15s',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.5,
          flexShrink: 0,
        }}
      >
        {/* Quick reactions — desktop only (mobile uses MoreVert menu) */}
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          onClick={() => setShowReactions(!showReactions)}
          sx={{ display: { xs: 'none', md: 'inline-flex' } }}
        >
          <AddReactionIcon size={18} />
        </IconButton>

        {/* Reply — desktop only (mobile uses MoreVert menu) */}
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          onClick={() => onReply?.(message.id)}
          sx={{ display: { xs: 'none', md: 'inline-flex' } }}
        >
          <ReplyIcon size={18} />
        </IconButton>

        {/* More menu */}
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          <MoreVertIcon size={18} />
        </IconButton>
      </Box>

      {/* Reaction picker */}
      {showReactions && (
        <Box
          sx={{
            position: 'absolute',
            right: 16,
            bgcolor: 'background.surface',
            borderRadius: 'lg',
            boxShadow: 'md',
            p: 0.5,
            display: 'flex',
            gap: 0.25,
            zIndex: 10,
          }}
        >
          {QUICK_REACTIONS.map((emoji) => (
            <IconButton
              key={emoji}
              size="sm"
              variant="plain"
              onClick={() => handleReactionClick(emoji)}
              sx={{
                fontSize: '1.25rem',
                minWidth: 32,
                minHeight: 32,
              }}
            >
              {EMOJI_MAP[emoji]}
            </IconButton>
          ))}
        </Box>
      )}

      {/* Actions menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        placement="bottom-end"
      >
        {isOwnMessage && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              // TODO: Open edit modal
              onEdit?.(message.id, message.content);
            }}
          >
            <ListItemDecorator>
              <EditIcon />
            </ListItemDecorator>
            Edit message
          </MenuItem>
        )}
        {isOwnMessage && (
          <MenuItem
            color="danger"
            onClick={() => {
              setMenuAnchor(null);
              onDelete?.(message.id);
            }}
          >
            <ListItemDecorator>
              <DeleteIcon />
            </ListItemDecorator>
            Delete message
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onReply?.(message.id);
          }}
        >
          <ListItemDecorator>
            <ReplyIcon />
          </ListItemDecorator>
          Reply in thread
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setShowReactions(true);
          }}
        >
          <ListItemDecorator>
            <AddReactionIcon />
          </ListItemDecorator>
          Add reaction
        </MenuItem>
      </Menu>

      {/* Image preview modal */}
      <Modal
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Sheet
          sx={{
            maxWidth: '90vw',
            maxHeight: '90vh',
            borderRadius: 'md',
            p: 1,
            boxShadow: 'lg',
            position: 'relative',
            bgcolor: 'background.surface',
          }}
        >
          <ModalClose />
          {previewImage && (
            <Box
              component="img"
              src={previewImage.url}
              alt={previewImage.name}
              sx={{
                maxWidth: '100%',
                maxHeight: 'calc(90vh - 48px)',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          )}
        </Sheet>
      </Modal>
    </Box>
  );
};

export default MessageItem;
